import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Cpu, TrendingUp, BarChart2, Terminal } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './AiInvestmentCalculatorPage/components/CalculationForm';
import ResultsOverview from './AiInvestmentCalculatorPage/components/ResultsOverview';
import InvestmentSchedule from './AiInvestmentCalculatorPage/components/InvestmentSchedule';
import ComparisonSidebar from './AiInvestmentCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './AiInvestmentCalculatorPage/components/TipsSidebar';
import InfoContent from './AiInvestmentCalculatorPage/components/InfoContent';

// Начальные данные формы
export const initialFormData = {
  initialInvestment: '100000', // Начальная сумма инвестиций
  investmentTerm: '5', // Срок инвестирования в годах
  riskLevel: 'moderate', // Уровень риска
  aiSectors: ['software', 'hardware'], // Выбранные сектора ИИ
  reinvestmentStrategy: 'compound', // Стратегия реинвестирования
  monthlyContribution: '10000', // Ежемесячный взнос
  targetCompanies: [], // Целевые компании
  investmentType: 'diversified', // Тип инвестиционной стратегии
  technologyFocus: ['machine_learning', 'neural_networks'], // Фокус технологий
  geographicFocus: ['usa', 'global'], // Географический фокус
};

// Типы инвестиционных стратегий
export const investmentStrategies = [
  { 
    id: 'diversified', 
    name: 'Диверсифицированная', 
    description: 'Распределение инвестиций между различными компаниями и технологиями ИИ' 
  },
  { 
    id: 'focused', 
    name: 'Целевая', 
    description: 'Концентрация на перспективных компаниях и технологиях' 
  },
  { 
    id: 'etf', 
    name: 'ETF ИИ', 
    description: 'Инвестиции через биржевые фонды, связанные с ИИ' 
  }
];

// Уровни риска
export const riskLevels = [
  { 
    id: 'low', 
    name: 'Низкий', 
    description: 'Консервативная стратегия с минимальными рисками' 
  },
  { 
    id: 'moderate', 
    name: 'Умеренный', 
    description: 'Сбалансированный подход с умеренным риском' 
  },
  { 
    id: 'high', 
    name: 'Высокий', 
    description: 'Агрессивная стратегия с потенциально высокой доходностью' 
  }
];

// Секторы ИИ
export const aiSectors = [
  {
    id: 'software',
    name: 'Программное обеспечение',
    description: 'Компании, разрабатывающие ИИ-софт'
  },
  {
    id: 'hardware',
    name: 'Аппаратное обеспечение',
    description: 'Производители специализированного оборудования'
  },
  {
    id: 'cloud',
    name: 'Облачные технологии',
    description: 'Облачные платформы и решения для ИИ'
  },
  {
    id: 'robotics',
    name: 'Робототехника',
    description: 'Робототехника и автоматизация с ИИ'
  }
];

// Технологический фокус
export const technologyFocus = [
  {
    id: 'machine_learning',
    name: 'Машинное обучение',
    description: 'Алгоритмы, обучающиеся на данных'
  },
  {
    id: 'neural_networks',
    name: 'Нейронные сети',
    description: 'Глубокое обучение и нейросети'
  },
  {
    id: 'natural_language',
    name: 'Обработка языка',
    description: 'Понимание и генерация текста'
  },
  {
    id: 'computer_vision',
    name: 'Компьютерное зрение',
    description: 'Анализ и распознавание изображений'
  }
];

// Географический фокус
export const geographicFocus = [
  {
    id: 'usa',
    name: 'США',
    description: 'Компании из Северной Америки'
  },
  {
    id: 'china',
    name: 'Китай',
    description: 'Компании из Китая и Азии'
  },
  {
    id: 'eu',
    name: 'Европа',
    description: 'Европейские технологические компании'
  },
  {
    id: 'global',
    name: 'Глобально',
    description: 'Международные компании и проекты'
  }
];

/**
 * Страница калькулятора инвестиций в ИИ
 */
const AiInvestmentCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // Данные инструмента
  const tool = useMemo(() => getToolById('ai-investment-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор инвестиций в ИИ | Gensio';
  const metaDescription = tool?.metaDescription || 'Профессиональный калькулятор инвестиций в технологии искусственного интеллекта с анализом доходности, рисков и прогнозированием.';

  // Управление состоянием
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [investmentSchedule, setInvestmentSchedule] = useState([]);
  const [showFullSchedule, setShowFullSchedule] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedInvestments, setSavedInvestments] = useState([]);

  // Обновление заголовка и мета-описания
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // Загрузка сохраненных инвестиционных планов
  useEffect(() => {
    const savedInvestmentsData = localStorage.getItem('savedAiInvestments');
    if (savedInvestmentsData) {
      try {
        setSavedInvestments(JSON.parse(savedInvestmentsData));
      } catch (e) {
        console.error("Ошибка загрузки сохраненных инвестиций:", e);
      }
    }
  }, []);

  // Обработка подтипа инструмента
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'low-risk':
          newState.riskLevel = 'low';
          newState.investmentType = 'etf';
          break;
        case 'high-risk':
          newState.riskLevel = 'high';
          newState.investmentType = 'focused';
          break;
        case 'machine-learning':
          newState.technologyFocus = ['machine_learning'];
          break;
        case 'neural-networks':
          newState.technologyFocus = ['neural_networks'];
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setInvestmentSchedule([]);
    setErrors({});
    setShowFullSchedule(false);
  }, [subToolId]);

  // Сохранение инвестиционного плана
  const saveInvestmentPlan = (name) => {
    if (!results) return;

    const newSavedInvestment = {
      id: Date.now().toString(),
      name: name || `Инвестиции в ИИ ${formData.initialInvestment}₽ на ${formData.investmentTerm} лет`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
    };

    const updatedSavedInvestments = [...savedInvestments, newSavedInvestment];
    setSavedInvestments(updatedSavedInvestments);
    localStorage.setItem('savedAiInvestments', JSON.stringify(updatedSavedInvestments));

    return newSavedInvestment;
  };

  // Удаление сохраненного инвестиционного плана
  const deleteSavedInvestment = (id) => {
    const updatedSavedInvestments = savedInvestments.filter(inv => inv.id !== id);
    setSavedInvestments(updatedSavedInvestments);
    localStorage.setItem('savedAiInvestments', JSON.stringify(updatedSavedInvestments));
  };

  // Загрузка сохраненного инвестиционного плана
  const loadSavedInvestment = (id) => {
    const investmentToLoad = savedInvestments.find(inv => inv.id === id);
    if (investmentToLoad) {
      setFormData(investmentToLoad.formData);
      setResults(investmentToLoad.results);
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* Основная область контента */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Заголовок страницы */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Cpu className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор инвестиций в ИИ'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* Форма калькулятора */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              investmentSchedule={investmentSchedule}
              setInvestmentSchedule={setInvestmentSchedule}
              saveInvestmentPlan={saveInvestmentPlan}
              investmentStrategies={investmentStrategies}
              riskLevels={riskLevels}
              aiSectors={aiSectors}
              technologyFocus={technologyFocus}
              geographicFocus={geographicFocus}
            />

            {/* Результаты расчета */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <InvestmentSchedule 
                  schedule={investmentSchedule} 
                  results={results}
                  showFullSchedule={showFullSchedule}
                  setShowFullSchedule={setShowFullSchedule}
                />
              </>
            )}

            {/* Информационный контент */}
            <InfoContent />
          </div>

          {/* Боковая панель */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* Панель сравнения инвестиционных планов */}
            <ComparisonSidebar 
              savedInvestments={savedInvestments}
              deleteSavedInvestment={deleteSavedInvestment}
              loadSavedInvestment={loadSavedInvestment}
            />

            {/* Панель советов */}
            <TipsSidebar />

            {/* Связанные инструменты */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* Глобальные стили */}
      <style jsx global>{`
        /* Базовые стили ввода */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Стили ошибок */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }
      `}</style>
    </div>
  );
};

export default AiInvestmentCalculatorPage;